import {
  Badge,
  Box,
  Button,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Spinner,
  Flex,
  SimpleGrid,
  Input,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeAccommodationImages } from "./dummyAccommodationImages";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";

// ✅ use your datepicker
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker";
import { UsersIcon } from "lucide-react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { PickDenguShareButton } from "../../Businesses/BusinessQrShareActions";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accommodation: any | null;
  initialCheckIn?: Date | null;
  initialCheckOut?: Date | null;
  initialGuests?: number;
  onBook?: (payload: any) => void; // { accommodation, room, checkIn, checkOut, guests }
};

const fallbackCenter = { lat: -13.9626, lng: 33.7741 };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const cardsBorderRadii ="8px";

const PICKDENGU_PUBLIC_WEB_URL = (
  process.env.REACT_APP_PUBLIC_WEB_URL ||
  process.env.REACT_APP_FRONTEND_URL ||
  process.env.REACT_APP_APP_URL ||
  "https://pickdengu.mw"
).replace(/\/+$/, "");

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const { theme } = useCustomTheme();
  return (
    <Box w="100%">
      <Text fontSize="xs" color={theme.textSubtle} mb={1}>
        {label}
      </Text>
      {children}
    </Box>
  );
}

function toISODate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function safeImagesFromRoom(room: any): string[] {
  if (!room) return [];
  const imgs = Array.isArray(room.images) ? room.images.filter(Boolean) : [];
  const cover = room.imageUrl ? [room.imageUrl] : [];
  const merged = [...cover, ...imgs].filter(Boolean);
  return Array.from(new Set(merged));
}

const AccommodationDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  accommodation,
  initialCheckIn,
  initialCheckOut,
  initialGuests,
  onBook,
}) => {
  const { theme } = useCustomTheme();

  const propertyImages = useMemo(
    () =>
      normalizeAccommodationImages(
        accommodation?.images,
        accommodation?.imageUrl,
      ),
    [accommodation],
  );

  const roomOptions = useMemo(() => {
    if (!accommodation) return [];
    const ro = Array.isArray(accommodation.roomOptions)
      ? accommodation.roomOptions
      : [];
    if (ro.length) return ro;

    return [
      {
        id: "default",
        title: accommodation.roomType || accommodation.type || "Room",
        pricePerNight: accommodation.pricePerNight ?? accommodation.price ?? 0,
        maxGuests: accommodation.maxGuests,
        availableRooms: accommodation.rooms ?? 1,
        images: accommodation.images || [],
        imageUrl: accommodation.imageUrl || accommodation.images?.[0] || "",
      },
    ];
  }, [accommodation]);

  const [activeProperty, setActiveProperty] = useState(0);
  const [activeRoomImg, setActiveRoomImg] = useState(0);

  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);

  const [guests, setGuests] = useState<number>(1);
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");

  const [availability, setAvailability] = useState<Record<string, number>>({});
  const [loadingAvail, setLoadingAvail] = useState(false);

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  const coords = useMemo(() => {
    const c = accommodation?.locationCoords;
    if (c && typeof c.lat === "number" && typeof c.lng === "number") {
      return { lat: c.lat, lng: c.lng };
    }
    return null;
  }, [accommodation]);

  const center = coords || fallbackCenter;

  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setCheckInDate(initialCheckIn || null);
    setCheckOutDate(initialCheckOut || null);
    setGuests(initialGuests || 1);
  }, [
    isOpen,
    accommodation?._id,
    initialCheckIn,
    initialCheckOut,
    initialGuests,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveProperty(0);
  }, [isOpen, accommodation?._id]);

  useEffect(() => {
    if (!isOpen) return;
    if (!roomOptions.length) return;
    setSelectedRoomId(roomOptions[0].id);
  }, [isOpen, accommodation?._id, roomOptions]);

  const selectedRoom =
    roomOptions.find((r: any) => r.id === selectedRoomId) ||
    roomOptions[0] ||
    null;

  const selectedRoomImages = useMemo(() => {
    const imgs = safeImagesFromRoom(selectedRoom);
    return imgs.length ? imgs : propertyImages;
  }, [selectedRoom, propertyImages]);

  useEffect(() => {
    setActiveRoomImg(0);
  }, [selectedRoomId, accommodation?._id]);

  const nights = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0;
    const a = new Date(checkInDate);
    const b = new Date(checkOutDate);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);
    const diff = Math.ceil((+b - +a) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  }, [checkInDate, checkOutDate]);

  const pricePerNight =
    selectedRoom?.pricePerNight ??
    accommodation?.pricePerNight ??
    accommodation?.price ??
    0;

  const total = nights * Number(pricePerNight || 0);

  useEffect(() => {
    const run = async () => {
      if (!accommodation?._id) return;
      if (!checkInDate || !checkOutDate) return;
      if (!API_BASE) return;

      const checkIn = toISODate(checkInDate);
      const checkOut = toISODate(checkOutDate);

      setLoadingAvail(true);
      try {
        const res = await axios.get(
          `${API_BASE}/api/v1/travel/accommodations/${accommodation._id}/availability`,
          {
            params: { checkIn, checkOut, guests },
          },
        );

        const map: Record<string, number> = {};
        for (const a of res.data?.availability || []) {
          map[a.roomId] = Number(a.available || 0);
        }
        setAvailability(map);

        const firstAvailable = roomOptions.find(
          (r: any) => (map[r.id] ?? 0) > 0,
        );
        if (firstAvailable) setSelectedRoomId(firstAvailable.id);
      } catch (e) {
        console.error("availability error", e);
        setAvailability({});
      } finally {
        setLoadingAvail(false);
      }
    };

    run();
  }, [accommodation?._id, checkInDate, checkOutDate, guests, roomOptions]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isLoaded) return;
    if (!mapRef.current) return;

    const t = window.setTimeout(() => {
      try {
        // @ts-ignore
        window.google?.maps?.event?.trigger(mapRef.current, "resize");
        mapRef.current?.panTo(center);
      } catch {}
    }, 200);

    return () => window.clearTimeout(t);
  }, [isOpen, isLoaded, center]);

  const canBook =
    !!accommodation &&
    accommodation.availability !== false &&
    !!selectedRoom &&
    !!checkInDate &&
    !!checkOutDate &&
    (availability[selectedRoom?.id] ?? selectedRoom?.availableRooms ?? 0) > 0;

  if (!accommodation) return null;

  const accommodationShareId = String(
    accommodation?._id || accommodation?.id || "",
  ).trim();

  const accommodationShareUrl = accommodationShareId
    ? `${PICKDENGU_PUBLIC_WEB_URL}/travel/accommodations/${encodeURIComponent(
        accommodationShareId,
      )}`
    : PICKDENGU_PUBLIC_WEB_URL;

  const accommodationShareName = accommodation?.name || "Accommodation";

  const accommodationShareText = [
    accommodationShareName,
    accommodation?.location,
    selectedRoom?.title,
    `MWK ${Number(pricePerNight || 0).toLocaleString()} per night`,
    accommodation?.description?.trim()?.slice(0, 160),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg={theme.overlay} />
      <ModalContent
        overflow="hidden"
        bg={theme.surface}
        border="1px solid"
        borderColor={theme.border}
        boxShadow={theme.shadowMd}
      >
        <ModalHeader color={theme.text} pb={2}>
          <Box>
            <Text
              fontSize={{ base: "lg", md: "2xl" }}
              fontWeight="800"
              color={theme.text}
              lineHeight="1.15"
              noOfLines={2}
              textTransform="uppercase"
            >
              {accommodation.name}
            </Text>
          </Box>
        </ModalHeader>{" "}
        <ModalCloseButton color={theme.text} />
        <ModalBody pb={6}>
          <Stack direction={{ base: "column", lg: "row" }} spacing={5}>
            {/* LEFT: Property gallery + Room gallery */}
            <Box flex="1">
              <Box>
                <Box>
                  <HStack spacing={2} align="center">
                    <Box
                      w="6px"
                      h="6px"
                      borderRadius={cardsBorderRadii}
                      bg={theme.inputBorderFocus}
                    />

                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      fontWeight="800"
                      color={theme.text}
                      letterSpacing="-0.01em"
                    >
                      {selectedRoom?.title || "Room photos"}
                    </Text>
                  </HStack>

                  <Text fontSize="xs" color={theme.textSubtle} mt={1}>
                    {selectedRoom?.title
                      ? "Preview photos for this selected room"
                      : "Preview photos for this accommodation"}
                  </Text>
                </Box>
                <Box
                  borderRadius={cardsBorderRadii}
                  overflow="hidden"
                  bg={theme.surfaceAlt}
                  h={{ base: "220px", md: "320px" }}
                  border="1px solid"
                  borderColor={theme.border}
                >
                  <Image
                    src={selectedRoomImages[activeRoomImg]}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>

                <HStack mt={3} spacing={2} overflowX="auto" pb={1}>
                  {selectedRoomImages.map((src: string, idx: number) => {
                    const shouldFillContainer = selectedRoomImages.length > 3;

                    return (
                      <Box
                        key={`${src}-${idx}`}
                        borderWidth={activeRoomImg === idx ? "2px" : "1px"}
                        borderColor={
                          activeRoomImg === idx
                            ? theme.inputBorderFocus
                            : theme.border
                        }
                        borderRadius={cardsBorderRadii}
                        overflow="hidden"
                        cursor="pointer"
                        onClick={() => setActiveRoomImg(idx)}
                        minW={
                          shouldFillContainer ? "calc(33.333% - 8px)" : "80px"
                        }
                        w={shouldFillContainer ? "calc(33.333% - 8px)" : "80px"}
                        h="56px"
                        bg={theme.surface}
                        boxShadow={
                          activeRoomImg === idx ? theme.shadowSm : "none"
                        }
                        flexShrink={0}
                      >
                        <Image src={src} w="100%" h="100%" objectFit="cover" />
                      </Box>
                    );
                  })}
                </HStack>

                {safeImagesFromRoom(selectedRoom).length === 0 && (
                  <Text fontSize="sm" color={theme.textSubtle} mt={2}>
                    This room has no specific photos yet — showing property
                    photos instead.
                  </Text>
                )}
              </Box>
            </Box>

            {/* RIGHT: Details + date picker + rooms + map */}
            <Box flex="1">
              <HStack spacing={2} flexWrap="wrap">
                <Badge
                  colorScheme={accommodation.availability ? "green" : "red"}
                >
                  {accommodation.availability ? "Available" : "Unavailable"}
                </Badge>
                {(accommodation.type || accommodation.roomType) && (
                  <Badge colorScheme="purple">
                    {accommodation.type || accommodation.roomType}
                  </Badge>
                )}
                {accommodation.freeCancellation && (
                  <Badge colorScheme="teal">Free cancellation</Badge>
                )}
                {accommodation.breakfastIncluded && (
                  <Badge colorScheme="orange">Breakfast included</Badge>
                )}
              </HStack>

              <Text mt={3} fontSize="sm" color={theme.textSubtle}>
                {accommodation.description || "No description provided."}
              </Text>

              {/* Date selection */}
              <Box
                mt={4}
                p={3}
                borderWidth="1px"
                borderRadius={cardsBorderRadii}
                borderColor={theme.border}
                bg={theme.surfaceAlt}
                boxShadow={theme.shadowSm}
              >
                <Flex gap={2} alignItems="flex-end" wrap="wrap">
                  <Box w={{ base: "100%", md: "auto" }}>
                    <FormRow label="">
                      <CustomDatePicker
                        width={{ base: "100%", md: "100%" }}
                        label="Check-in"
                        selected={checkInDate}
                        onChange={(d) => {
                          setCheckInDate(d);
                          if (d && checkOutDate && checkOutDate <= d) {
                            setCheckOutDate(null);
                          }
                        }}
                        placeholder="Pick check-in"
                      />
                    </FormRow>
                  </Box>

                  <Box w={{ base: "100%", md: "auto" }}>
                    <FormRow label="">
                      <CustomDatePicker
                        width={{ base: "100%", md: "100%" }}
                        label="Check-out"
                        selected={checkOutDate}
                        onChange={setCheckOutDate}
                        placeholder="Pick check-out"
                      />
                    </FormRow>
                  </Box>

                  <Flex
                    color={theme.textMuted}
                    gap={2}
                    flexDirection="column"
                    w={{ base: "100%", md: "auto" }}
                  >
                    <Flex gap={"6px"} alignItems={"center"}>
                      <UsersIcon size={"11"} />
                      <Text fontSize="11px" color={theme.textSubtle}>
                        Guests
                      </Text>
                    </Flex>

                    <HStack
                      spacing={0}
                      border="1px solid"
                      borderColor={theme.inputBorder}
                      borderRadius={cardsBorderRadii}
                      overflow="hidden"
                      bg={theme.inputBg}
                      w={{ base: "100%", md: "150px" }}
                    >
                      <Button
                        borderRadius="0"
                        variant="ghost"
                        minW="44px"
                        onClick={() =>
                          setGuests((prev) => Math.max(1, prev - 1))
                        }
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        -
                      </Button>

                      <Box
                        flex="1"
                        textAlign="center"
                        color={theme.text}
                        fontWeight="600"
                        py={2}
                        borderLeft="1px solid"
                        borderRight="1px solid"
                        borderColor={theme.inputBorder}
                      >
                        {guests}
                      </Box>

                      <Button
                        borderRadius="0"
                        variant="ghost"
                        minW="44px"
                        onClick={() => setGuests((prev) => prev + 1)}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        +
                      </Button>
                    </HStack>
                  </Flex>
                </Flex>

                {loadingAvail && (
                  <HStack mt={2}>
                    <GlobalSpinner />
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Checking availability…
                    </Text>
                  </HStack>
                )}
              </Box>

              {/* Rooms list */}
              <Box mt={4}>
                <Text fontWeight="bold" mb={2} color={theme.text}>
                  Choose room
                </Text>

                <Stack spacing={2}>
                  {roomOptions.map((r: any) => {
                    const avail =
                      checkInDate && checkOutDate
                        ? (availability[r.id] ?? 0)
                        : (r.availableRooms ?? 0);
                    const soldOut =
                      !!checkInDate && !!checkOutDate && avail <= 0;
                    const active = r.id === selectedRoomId;

                    return (
                      <Box
                        key={r.id}
                        borderWidth={active ? "2px" : "1px"}
                        borderColor={
                          active ? theme.inputBorderFocus : theme.border
                        }
                        borderRadius={cardsBorderRadii}
                        p={3}
                        bg={active ? theme.surfaceAlt : theme.surface}
                        cursor={soldOut ? "not-allowed" : "pointer"}
                        opacity={soldOut ? 0.6 : 1}
                        onClick={() => !soldOut && setSelectedRoomId(r.id)}
                        boxShadow={active ? theme.shadowSm : "none"}
                        transition="all 0.15s ease"
                      >
                        <HStack justify="space-between" align="start">
                          <Box>
                            <Text fontWeight="semibold" color={theme.text}>
                              {r.title}
                            </Text>
                            <Text fontSize="sm" color={theme.textSubtle}>
                              MWK{" "}
                              {Number(r.pricePerNight || 0).toLocaleString()} /
                              night
                            </Text>
                          </Box>

                          {checkInDate && checkOutDate ? (
                            <Badge colorScheme={soldOut ? "red" : "green"}>
                              {soldOut ? "Sold out" : `${avail} left`}
                            </Badge>
                          ) : (
                            <Badge variant="subtle" colorScheme="gray">
                              Pick dates
                            </Badge>
                          )}
                        </HStack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              {/* Total + Book */}
              <Box
                mt={5}
                p={4}
                borderWidth="1px"
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
                borderColor={theme.border}
                boxShadow={theme.shadowSm}
              >
                <Text fontSize="sm" color={theme.textMuted}>
                  Total
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
                  MWK {Number(total || 0).toLocaleString()}
                </Text>
                <Text fontSize="sm" color={theme.textSubtle}>
                  {nights} nights × MWK{" "}
                  {Number(pricePerNight || 0).toLocaleString()}
                </Text>

                <HStack
                  mt={3}
                  flexDirection={{ base: "column", sm: "row" }}
                  align="stretch"
                >
                  <Button
                    w={{ base: "100%", sm: "auto" }}
                    variant="outline"
                    onClick={onClose}
                    borderColor={theme.borderStrong}
                    color={theme.text}
                    _hover={{ bg: theme.hover }}
                  >
                    Close
                  </Button>
                  <Button
                    w={{ base: "100%", sm: "auto" }}
                    color={theme.textOnPrimary}
                    bgImage={theme.gradients.primaryButton}
                    _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                    boxShadow={theme.shadowSm}
                    isDisabled={!canBook}
                    onClick={() => {
                      if (!checkInDate || !checkOutDate) return;
                      onBook?.({
                        accommodation,
                        room: selectedRoom,
                        checkIn: toISODate(checkInDate),
                        checkOut: toISODate(checkOutDate),
                        guests,
                      });
                    }}
                    whiteSpace="nowrap"
                    borderRadius="5px"
                  >
                    Continue to Checkout
                  </Button>

                  <PickDenguShareButton
                    shareTitle={`${accommodationShareName} on PickDengu`}
                    shareText={accommodationShareText}
                    shareUrl={accommodationShareUrl}
                    modalTitle={`Share ${accommodationShareName}`}
                    modalDescription="Share this accommodation details page."
                    buttonProps={{
                      w: {
                        base: "100%",
                        sm: "115px",
                      },
                      variant: "outline",
                      borderColor: theme.borderStrong,
                      color: theme.text,
                      bg: theme.surface,
                      boxShadow: theme.shadowSm,
                      borderRadius: {cardsBorderRadii},
                      _hover: {
                        bg: theme.hover,
                      },
                    }}
                  />
                </HStack>

                {/* Map */}
                <Box
                  mt={3}
                  borderWidth="1px"
                  borderRadius={cardsBorderRadii}
                  overflow="hidden"
                  borderColor={theme.border}
                  bg={theme.surface}
                >
                  <Box
                    px={3}
                    py={2}
                    bg={theme.surfaceAlt}
                    borderBottomWidth="1px"
                    borderColor={theme.border}
                  >
                    <Text fontWeight="semibold" color={theme.text}>
                      Location on map
                    </Text>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      {accommodation.location || "—"}
                    </Text>
                  </Box>

                  {!API_KEY ? (
                    <Box p={3}>
                      <Text fontSize="sm" color="red.500">
                        Missing REACT_APP_GOOGLE_MAPS_API_KEY — cannot load map.
                      </Text>
                    </Box>
                  ) : loadError ? (
                    <Box p={3}>
                      <Text fontSize="sm" color="red.500">
                        Maps failed to load. Check API key restrictions +
                        billing.
                      </Text>
                    </Box>
                  ) : !isLoaded ? (
                    <Flex align="center" justify="center" h="220px" gap={2}>
                      <GlobalSpinner />
                      <Text fontSize="sm" color={theme.textSubtle}>
                        Loading map…
                      </Text>
                    </Flex>
                  ) : (
                    <Box h="220px" w="100%">
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={center}
                        zoom={coords ? 15 : 11}
                        options={{
                          disableDefaultUI: true,
                          zoomControl: true,
                          clickableIcons: false,
                        }}
                        onLoad={(map) => {
                          mapRef.current = map;
                        }}
                        onUnmount={() => {
                          mapRef.current = null;
                        }}
                      >
                        {coords && <MarkerF position={coords} />}
                      </GoogleMap>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccommodationDetailsModal;
